import { safeGetItem, safeSetItem, safeRemoveItem } from './storage';
import logger from './logger';

/**
 * Images de fond du récap partageable.
 *
 * Prises sur le téléphone au moment où l'élève choisit une vidéo de série
 * (avant même l'envoi), pour ne jamais attendre le traitement serveur. Gardées
 * dans localStorage sous l'identifiant de la séance assignée — le même que
 * celui du récap (utils/sessionRecap.js). Elles ne quittent pas l'appareil.
 */
const PREFIX = 'kaiylo_recap_frames_';
const INDEX_KEY = 'kaiylo_recap_frames_index';
export const MAX_VIDEO_FRAMES = 4;
export const MAX_SESSIONS = 3;
const FRAME_W = 540;
const FRAME_H = 960;
const PHOTO_W = 1080;
const PHOTO_H = 1920;
const CAPTURE_TIMEOUT_MS = 4000;

const readJson = (key, fallback) => {
  const raw = safeGetItem(key);
  if (!raw) return fallback;
  try {
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
};

const evictOldestUntil = (index, maxLength) => {
  while (index.length > maxLength) {
    safeRemoveItem(`${PREFIX}${index.shift()}`);
  }
};

// Les séances les plus anciennes partent au-delà de MAX_SESSIONS : le
// stockage reste à ~1 Mo par séance, 3 séances.
//
// L'éviction a lieu AVANT l'écriture (pas seulement après) : si le stockage
// est déjà plein, il faut libérer de la place avant d'espérer écrire quoi que
// ce soit. Si l'écriture échoue malgré tout (quota dépassé), on libère une
// séance de plus — jamais celle en cours d'écriture — et on retente une fois.
// La séance courante rejoint l'index si l'écriture a réussi, ou si une
// version antérieure de ses données est encore sur le disque : sinon une
// séance déjà indexée avant cet appel disparaîtrait de l'index sans que sa
// clé soit supprimée (orpheline, jamais nettoyée par la suite).
const writeSessionFrames = (sessionId, frames) => {
  const index = readJson(INDEX_KEY, []).filter((id) => id !== sessionId);
  evictOldestUntil(index, MAX_SESSIONS - 1);
  // L'index sur disque doit refléter les évictions même si l'écriture de la
  // séance courante échoue ensuite : jamais de séance évincée qui traîne dans
  // l'index alors que ses données ont déjà disparu.
  safeSetItem(INDEX_KEY, JSON.stringify(index));
  const payload = JSON.stringify(frames);
  let ok = safeSetItem(`${PREFIX}${sessionId}`, payload);
  if (!ok && index.length > 0) {
    safeRemoveItem(`${PREFIX}${index.shift()}`);
    safeSetItem(INDEX_KEY, JSON.stringify(index));
    ok = safeSetItem(`${PREFIX}${sessionId}`, payload);
  }
  if (ok || safeGetItem(`${PREFIX}${sessionId}`)) index.push(sessionId);
  safeSetItem(INDEX_KEY, JSON.stringify(index));
  return ok;
};

/**
 * Libère la place prise par les images des autres séances (le récap passe
 * avant ses images : utils/sessionRecap.js l'appelle quand l'écriture d'un
 * récap échoue). Toutes les séances indexées partent, sauf `sessionId`, et
 * l'index est réécrit. `includeCurrent` supprime aussi celles de `sessionId`,
 * en dernier recours.
 */
export const evictRecapFramesExcept = (sessionId, { includeCurrent = false } = {}) => {
  const index = readJson(INDEX_KEY, []);
  const kept = [];
  index.forEach((id) => {
    if (id === sessionId && !includeCurrent) kept.push(id);
    else safeRemoveItem(`${PREFIX}${id}`);
  });
  if (includeCurrent && sessionId) safeRemoveItem(`${PREFIX}${sessionId}`);
  safeSetItem(INDEX_KEY, JSON.stringify(kept));
};

export const loadRecapFrames = (sessionId) => {
  if (!sessionId) return [];
  const parsed = readJson(`${PREFIX}${sessionId}`, []);
  return Array.isArray(parsed) ? parsed : [];
};

export const hasRecapFrame = (sessionId, exerciseIndex, setIndex) =>
  loadRecapFrames(sessionId).some(
    (f) => f.source === 'video' && f.exerciseIndex === exerciseIndex && f.setIndex === setIndex
  );

export const saveRecapFrame = (sessionId, frame) => {
  if (!sessionId || !frame?.dataUrl) return false;
  const frames = loadRecapFrames(sessionId);
  const source = frame.source === 'photo' ? 'photo' : 'video';
  const capturedAt = new Date().toISOString();
  let next;
  if (source === 'photo') {
    next = [...frames.filter((f) => f.source !== 'photo'), { ...frame, id: 'photo', source, capturedAt }];
  } else {
    const { exerciseIndex, setIndex } = frame;
    if (!Number.isFinite(exerciseIndex) || !Number.isFinite(setIndex)) return false;
    if (frames.some((f) => f.source === 'video' && f.exerciseIndex === exerciseIndex && f.setIndex === setIndex)) {
      return false;
    }
    if (frames.filter((f) => f.source === 'video').length >= MAX_VIDEO_FRAMES) return false;
    next = [...frames, { ...frame, id: `video-${exerciseIndex}-${setIndex}`, source, capturedAt }];
  }
  return writeSessionFrames(sessionId, next);
};

/** Dessine `source` (sw × sh) en couvrant tout le canvas W × H, centré. */
export const drawCover = (ctx, source, sw, sh, W, H) => {
  const scale = Math.max(W / sw, H / sh);
  const dw = sw * scale;
  const dh = sh * scale;
  ctx.drawImage(source, (W - dw) / 2, (H - dh) / 2, dw, dh);
};

/**
 * Une capture, prise vers la première seconde de la vidéo, en JPEG 540 × 960.
 * Résout `null` sur toute erreur ou au bout de CAPTURE_TIMEOUT_MS ; ne rejette
 * jamais. Aucun play() : charger et chercher dans un blob est permis sans
 * geste sur WebKit.
 */
const rawCaptureVideoFrame = (file) =>
  new Promise((resolve) => {
    if (!file || typeof document === 'undefined' || typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') {
      resolve(null);
      return;
    }
    const video = document.createElement('video');
    const url = URL.createObjectURL(file);
    let done = false;
    let timer = null;
    let rvfcHandle = null;
    let seekFallbackTimer = null;
    const finish = (result) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      clearTimeout(seekFallbackTimer);
      if (typeof video.cancelVideoFrameCallback === 'function' && rvfcHandle != null) {
        try {
          video.cancelVideoFrameCallback(rvfcHandle);
        } catch {
          // Sans conséquence : la vidéo est libérée juste après de toute façon.
        }
      }
      video.onerror = null;
      video.onloadedmetadata = null;
      video.onseeked = null;
      video.removeAttribute('src');
      try {
        video.load();
      } catch {
        // jsdom ou WebView sans décodeur : rien à libérer.
      }
      URL.revokeObjectURL(url);
      resolve(result);
    };
    timer = setTimeout(() => finish(null), CAPTURE_TIMEOUT_MS);

    video.muted = true;
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.preload = 'auto';
    video.onerror = () => finish(null);
    video.onloadedmetadata = () => {
      const duration = Number.isFinite(video.duration) ? video.duration : 0;
      try {
        video.currentTime = Math.min(1, duration / 2);
      } catch {
        finish(null);
      }
    };
    const draw = () => {
      if (done) return;
      try {
        const w = video.videoWidth;
        const h = video.videoHeight;
        if (!w || !h) {
          // Dimensions pas encore prêtes (fréquent sur iOS juste après le
          // seek) : mieux vaut renoncer qu'étirer une frame déformée.
          finish(null);
          return;
        }
        const canvas = document.createElement('canvas');
        canvas.width = FRAME_W;
        canvas.height = FRAME_H;
        const ctx = canvas.getContext('2d');
        drawCover(ctx, video, w, h, FRAME_W, FRAME_H);
        finish(canvas.toDataURL('image/jpeg', 0.72));
      } catch (error) {
        logger.debug('Recap frame capture failed:', error);
        finish(null);
      }
    };
    video.onseeked = () => {
      // Sur iOS/Safari, une vidéo en pause après un seek peut ne jamais
      // présenter de frame à requestVideoFrameCallback : on court les deux en
      // parallèle et on dessine avec celui qui arrive en premier.
      if (typeof video.requestVideoFrameCallback === 'function') {
        rvfcHandle = video.requestVideoFrameCallback(() => draw());
        seekFallbackTimer = setTimeout(draw, 300);
      } else {
        draw();
      }
    };
    video.src = url;
  });

// Un seul <video> caché décode à la fois : Safari est capricieux quand
// plusieurs éléments vidéo décodent en parallèle sur un iPhone bas de gamme.
let captureChain = Promise.resolve();

export const captureVideoFrame = (file) => {
  captureChain = captureChain.then(() => rawCaptureVideoFrame(file)).catch((error) => {
    logger.debug('Recap frame capture crashed:', error);
    return null;
  });
  return captureChain;
};

/** Réservé aux tests : remet la file de captures à zéro entre deux specs. */
export const __resetCaptureQueueForTests = () => {
  captureChain = Promise.resolve();
};

const decodeImage = async (file) => {
  if (typeof createImageBitmap === 'function') {
    try {
      // resizeHeight fait faire le sous-échantillonnage par le décodeur
      // lui-même : une photo de 12 Mpx ne passe jamais en pleine résolution
      // par la mémoire d'un iPhone bas de gamme.
      return await createImageBitmap(file, {
        imageOrientation: 'from-image',
        resizeHeight: PHOTO_H,
        resizeQuality: 'high'
      });
    } catch {
      // Ancien navigateur ou option non supportée : l'élément Image applique
      // lui-même l'orientation EXIF.
    }
  }
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('image decode failed'));
    };
    img.src = url;
  });
};

/** Photo de la galerie recadrée en 1080 × 1920, JPEG. `null` si illisible. */
export const prepareRecapPhoto = async (file) => {
  try {
    const source = await decodeImage(file);
    const sw = source.width || source.naturalWidth;
    const sh = source.height || source.naturalHeight;
    const canvas = document.createElement('canvas');
    canvas.width = PHOTO_W;
    canvas.height = PHOTO_H;
    drawCover(canvas.getContext('2d'), source, sw, sh, PHOTO_W, PHOTO_H);
    source.close?.();
    return canvas.toDataURL('image/jpeg', 0.75);
  } catch (error) {
    logger.debug('Recap photo could not be prepared:', error);
    return null;
  }
};
